import { useEffect, useState } from 'react';

import { ModalFrame } from 'components/ui/modal-frame';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';
import { getUserGroupList } from 'services/dashboard.service';

type Props = {
  player?: string;
  returnTotal?: (value: number) => void;
  cycleLabel: string;
};

const MODAL_ID = 'group-detail-extrato';

type GroupMember = { _id: string; chave: string; nome: string; grupo: string };

const CELL_CLASSES = 'px-4 py-2';

export function GroupDetailModalExtrato({ player, returnTotal, cycleLabel }: Props) {
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();

  // `undefined` enquanto o grupo não chegou: o diálogo só abre com a resposta.
  const [members, setMembers] = useState<GroupMember[]>();

  const isOpen = openModal === MODAL_ID;

  useEffect(() => {
    if (!user || !cycleLabel || !isOpen) return;

    let active = true;

    // Falha de leitura abre o diálogo vazio, em vez de deixar o clique sem resposta (MODH-06).
    getUserGroupList(player, cycleLabel)
      .then(data => (Array.isArray(data) ? (data as GroupMember[]) : []))
      .catch(error => {
        console.error('Erro ao carregar os integrantes do grupo:', error);
        return [] as GroupMember[];
      })
      .then(data => {
        if (!active) return;

        setMembers(data);
        returnTotal?.(data.length);
      });

    return () => {
      active = false;
    };
  }, [user, player, cycleLabel, returnTotal, isOpen]);

  if (!members) return null;

  if (!members.length) {
    return (
      <ModalFrame isOpen={isOpen} title="Grupo" onClose={closeModal}>
        <p className="m-0 px-5 py-6 text-sm text-content lg:px-7">Nenhum integrante encontrado neste grupo.</p>
      </ModalFrame>
    );
  }

  return (
    <ModalFrame isOpen={isOpen} title={`Grupo ${members[0].grupo}`} onClose={closeModal}>
      <div className="flex flex-col gap-4 px-5 py-6 text-sm text-content lg:px-7">
        {/* Região com rolagem própria precisa de foco pelo teclado (WCAG 2.1.1). */}
        <div
          role="region"
          aria-label="Integrantes do grupo"
          tabIndex={0}
          className="max-h-[50vh] overflow-auto rounded-jj-lg border border-surface-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
        >
          <table className="w-full border-collapse text-left">
            <thead className="bg-surface-100">
              <tr>
                <th scope="col" className={`${CELL_CLASSES} w-36 font-bold`}>
                  Matrícula
                </th>
                <th scope="col" className={`${CELL_CLASSES} font-bold`}>
                  Nome
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map(member => (
                <tr key={member._id} className="border-t border-surface-200">
                  <td className={CELL_CLASSES}>{member.chave}</td>
                  <td className={CELL_CLASSES}>{member.nome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="m-0">
          Total de funcionários do grupo: <b className="font-bold text-brand-blue-dark">{members.length} pessoas.</b>
        </p>
      </div>
    </ModalFrame>
  );
}
